import { Component, computed, DestroyRef, OnDestroy, OnInit, viewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { VehiclesSelectors } from '@states/vehicles/vehicles.selector-types';
import { VehiclesActions } from '@states/vehicles/vehicles.action-types';
import { SharedActions } from '@states/shared/shared.action-types';
import * as MenuActions from '@states/menu/menu.actions';
import { PreloaderColor } from '@enums/shared.enum';
import { MenuKey } from '@enums/menu.enum';
import { features } from '@consts/text.const';
import { UiVehicleDialogComponent } from './dialogs/ui-vehicle-dialog/ui-vehicle-dialog.component';
import { VehiclesUploadDialogComponent } from './dialogs/vehicles-upload-dialog/vehicles-upload-dialog.component';
import { UiKitModule } from '../../../../shared/ui-kit/ui-kit.module';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { VehiclesTableComponent } from './components/vehicles-table/vehicles-table.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { DatabaseTabs, DatabaseTabsComponent } from '../../components/database-tabs/database-tabs.component';
import { UnsavedVehiclesTableComponent } from './components/unsaved-vehicles-table/unsaved-vehicles-table.component';
import { ConfirmDialogModel, UiButtonComponent, UiSelectedItemsSnackbarComponent } from '@ui';
import { openDialogByParams } from '@consts/open-dialog-by-params.consts';
import { RouteModalService } from '@ui';
import { ActivatedRoute, Router } from '@angular/router';
import { syncQueryParam } from '../../../../helpers/sync-query-param.helper';
import { UiSearchComponent } from '../../../../shared/ui-kit/ui-search/ui-search.component';
import { UiNoDataComponent } from '@ui';
import { UiPreloaderComponent } from '../../../../shared/ui-kit/ui-preloader/ui-preloader.component';
import { filter, take } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { VehicleGroupsModel } from '@models/vehicle-groups.model';
import { VehicleGroupsDialogService } from '../../../../services/vehicle-groups-dialog.service';

type TabTypes = 'existing' | 'unsaved';
type ViewState = 'LOADING' | 'HAS_DATA' | 'NO_RESULTS' | 'INITIAL_EMPTY';

enum VehicleTab {
  existing = 'existing',
  unsaved = 'unsaved',
}

@Component({
  selector: 'vehicles',
  templateUrl: './vehicles.component.html',
  styleUrls: ['./vehicles.component.scss'],
  imports: [
    UiKitModule,
    ScrollingModule,
    FormsModule,
    VehiclesTableComponent,
    TranslatePipe,
    DatabaseTabsComponent,
    UnsavedVehiclesTableComponent,
    ReactiveFormsModule,
    UiSearchComponent,
    UiNoDataComponent,
    UiButtonComponent,
    UiPreloaderComponent,
    UiSelectedItemsSnackbarComponent,
  ],
})
export class VehiclesComponent implements OnInit, OnDestroy {
  public selectExistingVehicles = this.store$.selectSignal(VehiclesSelectors.selectSavedVehicles);
  public selectUnsavedVehicles = this.store$.selectSignal(VehiclesSelectors.selectUnsavedVehicles);

  public isLoadingSavedVehicles = this.store$.selectSignal(VehiclesSelectors.selectIsLoadingSavedVehicles);
  public isLoadingUnsavedVehicles = this.store$.selectSignal(VehiclesSelectors.selectIsLoadingUnsavedVehicles);

  public existingTable = viewChild<VehiclesTableComponent>('existingTable');

  protected readonly currentTab = syncQueryParam<TabTypes>('tab', { defaultValue: 'existing' });

  protected readonly existingCount = computed(() => this.selectExistingVehicles().length);
  protected readonly unsavedCount = computed(() => this.selectUnsavedVehicles().length);

  protected readonly noResults = features.vehicles.noResults;
  protected readonly noSaved = features.vehicles.noSaved;

  protected readonly PreloaderColor = PreloaderColor;

  protected vehiclePageTabs: DatabaseTabs<TabTypes>[] = [
    {
      value: 'existing',
      labelKey: 'VEHICLES.BUTTONS.EXISTING_VEHICLES',
      iconName: 'ui-icon-object-vehicle',
      count: this.existingCount,
    },
    {
      value: 'unsaved',
      labelKey: 'VEHICLES.BUTTONS.UNSAVED_VEHICLES',
      iconName: 'ui-icon-unsaved-vehicles',
      count: this.unsavedCount,
    },
  ];

  protected searchQuery = syncQueryParam<string>('search');

  public readonly vm$ = computed(() => {
    const isLoadingSaved = this.isLoadingSavedVehicles();
    const isLoadingUnsaved = this.isLoadingUnsavedVehicles();
    const currentTab = this.currentTab();
    const searchQuery = this.searchQuery();

    const existingVehicles = this.selectExistingVehicles();
    const filteredExisting = !searchQuery
      ? existingVehicles
      : existingVehicles.filter(
          (p) =>
            p?.plate?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            p?.owner?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            p?.color?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            p?.make?.toLowerCase().includes(searchQuery.toLowerCase()),
        );

    let existingViewState: ViewState;
    if (isLoadingSaved) existingViewState = 'LOADING';
    else if (existingVehicles.length > 0 && searchQuery && !filteredExisting.length)
      existingViewState = 'NO_RESULTS';
    else if (existingVehicles.length > 0) existingViewState = 'HAS_DATA';
    else existingViewState = 'INITIAL_EMPTY';

    const unsavedVehicles = this.selectUnsavedVehicles();
    const filteredUnsaved = !searchQuery
      ? unsavedVehicles
      : unsavedVehicles.filter((p) => p?.plate?.toLowerCase().includes(searchQuery.toLowerCase()));

    let unsavedViewState: ViewState;
    if (isLoadingUnsaved) unsavedViewState = 'LOADING';
    else if (unsavedVehicles.length > 0 && searchQuery && !filteredUnsaved.length)
      unsavedViewState = 'NO_RESULTS';
    else if (unsavedVehicles.length > 0) unsavedViewState = 'HAS_DATA';
    else unsavedViewState = 'INITIAL_EMPTY';

    const existingTable = this.existingTable();
    const selectedPlates = existingTable?.selectedPlatesRows() ?? new Set<string>();

    return {
      currentTab,
      searchQuery,
      existing: {
        viewState: existingViewState,
        vehicles: filteredExisting,
        hasSelection: selectedPlates.size > 0,
        selectedRows: selectedPlates,
      },
      unsaved: {
        viewState: unsavedViewState,
        vehicles: filteredUnsaved,
      },
    };
  });

  constructor(
    private store$: Store,
    private dialog: MatDialog,
    private translateService: TranslateService,
    private destroyRef: DestroyRef,
    private modalService: RouteModalService,
    private route: ActivatedRoute,
    private router: Router,
    private vehicleDialogService: VehicleGroupsDialogService,
  ) {}

  public ngOnInit(): void {
    this.store$.dispatch(
      MenuActions.setActiveLevel2Menu({
        activeLevel2: MenuKey.vehicles,
      }),
    );
    this.store$.dispatch(VehiclesActions.getVehicles());
    this.store$.dispatch(VehiclesActions.getUnsavedVehicles());

    this.modalService.initModalListeners(
      [
        {
          queryParam: openDialogByParams.addVehicles,
          open: () => this.openAddVehicleDialog(),
        },
        {
          queryParam: openDialogByParams.addVehiclesFromCsv,
          open: () => this.openAddVehicleFromCsvDialog(),
        },
      ],
      this.destroyRef,
    );
  }

  public onChangeActive(tab: TabTypes): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        search: null,
      },
      queryParamsHandling: 'merge',
    });
    this.currentTab.set(tab);
  }

  public add() {
    this.modalService.openModal(openDialogByParams.addVehicles);
  }

  public ngOnDestroy(): void {
    this.store$.dispatch(VehiclesActions.resetToInitialState());
  }

  public addCsv() {
    this.modalService.openModal(openDialogByParams.addVehiclesFromCsv);
  }

  public removeSelectedExisting(selectedRows: Set<string>): void {
    this.store$.dispatch(
      SharedActions.showConfirmModal({
        options: {
          type: ConfirmDialogModel.ConfirmDialogType.CONFIRM,
          msg: this.translateService.instant('DELETE_VEHICLE_MODAL.MSG_BY_SELECTED'),
          title: this.translateService.instant('DELETE_VEHICLE_MODAL.TITLE'),
          confirm: this.translateService.instant('CONFIRM_MODALS.COMMON_YES'),
          disableClose: true,
          params: {
            plates: Array.from(selectedRows),
          },
        },
      }),
    );
  }

  protected openAddVehicleToGroupsDialog(): void {
    const selectedPlates =
      this.existingTable()?.selectedPlatesRows() ?? this.vm$().existing.selectedRows;

    if (selectedPlates.size === 0) {
      return;
    }

    const vehicleIds = this.selectExistingVehicles()
      .filter((vehicle) => selectedPlates.has(vehicle.plate))
      .map((vehicle) => vehicle.id)
      .filter((id): id is number => id != null);

    const data: VehicleGroupsModel.AddVehicleToGroupsDialogData = {
      vehicleType: this.currentTab() === VehicleTab.existing
        ? VehicleGroupsModel.AddVehicleToGroupDialogTab.ExistingVehicles
        : VehicleGroupsModel.AddVehicleToGroupDialogTab.UnsavedVehicles,
      vehicleIds,
    };

    this.vehicleDialogService
      .openAddVehicleToGroups(data)
      .afterClosed()
      .pipe(
        filter(Boolean),
        take(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.clearSelectedVehicles());
  }

  protected selectAllDisplayedVehicles(): void {
    const displayedPlates = this.getDisplayedVehiclePlates();
    const table = this.existingTable();
    if (!table || displayedPlates.length === 0) {
      return;
    }

    const selected = table.selectedPlatesRows();
    const missingDisplayedPlates = displayedPlates.filter(
      (plate) => !selected.has(plate),
    );

    if (missingDisplayedPlates.length === 0) {
      return;
    }

    table.applyForcedSelection([...selected, ...missingDisplayedPlates]);
  }

  protected clearSelectedVehicles(): void {
    this.existingTable().applyForcedSelection([]);
  }

  private getDisplayedVehiclePlates(): string[] {
    const vehicles = this.vm$().existing.vehicles;
    return vehicles
      .map((v) => String(v?.plate ?? '').trim())
      .filter((plate) => plate.length > 0);
  }

  private openAddVehicleDialog() {
    const dialogRef = this.dialog.open(UiVehicleDialogComponent, {
      width: '540px',
      disableClose: true,
    });

    return dialogRef;
  }

  private openAddVehicleFromCsvDialog() {
    const dialogRef = this.dialog.open(VehiclesUploadDialogComponent, {
      width: '500px',
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((name) => {
      this.store$.dispatch(VehiclesActions.getVehicles());
    });

    return dialogRef;
  }
}
